import React, { useState, useEffect, useRef } from 'react';

// Simple SVG Icons as fallback
const XMarkIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const PlayIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
  </svg>
);

const ChevronLeftIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
  </svg>
);

const ChevronRightIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
  </svg>
);

interface MediaItem {
  id: string;
  type: 'image' | 'video';
  src: string;
  thumbnail?: string;
  alt: string;
}

const MediaGallery: React.FC = () => {
  const [selectedMedia, setSelectedMedia] = useState<MediaItem | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const lightboxRef = useRef<HTMLDivElement>(null);

  const mediaItems: MediaItem[] = [
    {
      id: '1',
      type: 'image',
      src: '/content/WhatsApp_Image_2025-05-27_at_11.56.59.jpeg',
      alt: 'Vereinsaktivitäten - Bild 1'
    },
    {
      id: '2',
      type: 'image',
      src: '/content/WhatsApp_Image_2025-05-27_at_11.59.11.jpg',
      alt: 'Vereinsaktivitäten - Bild 2'
    },
    {
      id: '4',
      type: 'image',
      src: '/content/WhatsApp_Image_2025-05-27_at_11.59.12.jpeg',
      alt: 'Vereinsaktivitäten - Bild 4'
    },
    {
      id: '5',
      type: 'image',
      src: '/content/WhatsApp_Image_2025-05-27_at_12.00.07.jpeg',
      alt: 'Vereinsaktivitäten - Bild 5'
    },
    {
      id: '6',
      type: 'image',
      src: '/content/WhatsApp_Image_2025-05-27_at_12.04.44.jpeg',
      alt: 'Vereinsaktivitäten - Bild 6'
    },
    {
      id: '7',
      type: 'image',
      src: '/content/WhatsApp_Image_2025-05-27_at_12.19.27.jpeg',
      alt: 'Vereinsaktivitäten - Bild 7'
    },
    {
      id: '8',
      type: 'image',
      src: '/content/WhatsApp_Image_2025-05-27_at_12.20.01.jpeg',
      alt: 'Vereinsaktivitäten - Bild 8'
    },
    {
      id: '9',
      type: 'image',
      src: '/content/WhatsApp_Image_2025-05-27_at_12.21.12.jpeg',
      alt: 'Vereinsaktivitäten - Bild 9'
    },
    {
      id: '10',
      type: 'image',
      src: '/content/WhatsApp_Image_2025-05-27_at_12.40.11.jpeg',
      alt: 'Vereinsaktivitäten - Bild 10'
    },
    {
      id: '11',
      type: 'image',
      src: '/content/WhatsApp_Image_2025-05-27_at_12.40.12.jpeg',
      alt: 'Vereinsaktivitäten - Bild 11'
    },
    {
      id: '12',
      type: 'video',
      src: '/content/WhatsApp_Video_2025-05-27_at_12.00.33.mp4',
      alt: 'Vereinsaktivitäten - Video 1'
    },
    {
      id: '13',
      type: 'video',
      src: '/content/WhatsApp_Video_2025-05-27_at_12.01.04.mp4',
      alt: 'Vereinsaktivitäten - Video 2'
    },
    {
      id: '14',
      type: 'image',
      src: '/content/WhatsApp_Image_2025-05-27_at_13.29.51.jpg',
      alt: 'Vereinsaktivitäten - Bild 12'
    },
    {
      id: '15',
      type: 'video',
      src: '/content/WhatsApp_Video_2025-05-26_22-29-15_3.mp4',
      alt: 'Vereinsaktivitäten - Video 3'
    },
    {
      id: '16',
      type: 'video',
      src: '/content/WhatsApp_Video_2025-05-26_22-29-15_4.mp4',
      alt: 'Vereinsaktivitäten - Video 4'
    },
    {
      id: '17',
      type: 'video',
      src: '/content/WhatsApp_Video_2025-05-26_22-31-23_1.mp4',
      alt: 'Vereinsaktivitäten - Video 5'
    }
  ];

  const openLightbox = (media: MediaItem) => {
    const index = mediaItems.findIndex(item => item.id === media.id);
    setCurrentIndex(index);
    setSelectedMedia(media);
  };

  const closeLightbox = () => {
    setSelectedMedia(null);
    setCurrentIndex(0);
  };

  const goToNext = () => {
    const nextIndex = (currentIndex + 1) % mediaItems.length;
    setCurrentIndex(nextIndex);
    setSelectedMedia(mediaItems[nextIndex]);
  };

  const goToPrevious = () => {
    const prevIndex = currentIndex === 0 ? mediaItems.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    setSelectedMedia(mediaItems[prevIndex]);
  };

  // Touch handlers for swipe functionality
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrevious();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!selectedMedia) return;
      
      if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'Escape') {
        closeLightbox();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedMedia, currentIndex]);

  return (
    <div className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-base text-blue-600 font-semibold tracking-wide uppercase">
            Medien Galerie
          </h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Impressionen aus unserem Vereinsleben
          </p>
          <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto">
            Entdecken Sie die Vielfalt unserer Aktivitäten in Bildern und Videos
          </p>
        </div>

        {/* Gallery Grid - Adjusted for smaller mobile thumbnails */}
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
          {mediaItems.map((item) => (
            <div
              key={item.id}
              className="relative group cursor-pointer overflow-hidden rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              onClick={() => openLightbox(item)}
            >
              <div className="aspect-w-16 aspect-h-9 bg-gray-200"> {/* Adjusted aspect ratio for consistency */}
                {item.type === 'image' ? (
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="w-full h-24 md:h-48 object-cover" /* h-24 for mobile, h-48 for md and up */
                    loading="lazy"
                  />
                ) : (
                  <div className="relative w-full h-24 md:h-48 bg-gray-900 flex items-center justify-center">
                    <video
                      src={item.src}
                      className="w-full h-full object-cover"
                      muted
                      preload="metadata"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                      <PlayIcon className="w-8 h-8 md:w-16 md:h-16 text-white opacity-80" /> {/* Smaller PlayIcon on mobile */}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Overlay - Adjusted PlayIcon size */}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  {item.type === 'video' && (
                    <PlayIcon className="w-6 h-6 md:w-12 md:w-12 text-white" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Enhanced Lightbox with Swipe Support */}
        {selectedMedia && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
            ref={lightboxRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="relative max-w-4xl max-h-full p-4 w-full">
              {/* Close Button */}
              <button
                onClick={closeLightbox}
                className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-all"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>

              {/* Navigation Arrows - Hidden on Mobile, Visible on Desktop */}
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-all hidden md:block"
              >
                <ChevronLeftIcon className="w-6 h-6" />
              </button>
              
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 p-3 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-all hidden md:block"
              >
                <ChevronRightIcon className="w-6 h-6" />
              </button>

              {/* Media Content */}
              <div className="flex items-center justify-center h-full">
                {selectedMedia.type === 'image' ? (
                  <img
                    src={selectedMedia.src}
                    alt={selectedMedia.alt}
                    className="max-w-full max-h-full object-contain rounded-lg"
                  />
                ) : (
                  <video
                    src={selectedMedia.src}
                    controls
                    autoPlay
                    className="max-w-full max-h-full rounded-lg"
                  >
                    Ihr Browser unterstützt das Video-Element nicht.
                  </video>
                )}
              </div>

              {/* Media Counter */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 px-3 py-1 bg-black bg-opacity-50 rounded-full text-white text-sm">
                {currentIndex + 1} / {mediaItems.length}
              </div>

              {/* Swipe Indicator for Mobile */}
              <div className="absolute bottom-16 left-1/2 transform -translate-x-1/2 z-10 text-white text-xs opacity-70 md:hidden">
                ← Wischen zum Navigieren →
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MediaGallery; 